import { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import {
  CertificationButton,
  CertificationModal,
} from "../../shared/ui";
import { bibliotecaApiError, getBibliotecaPreview } from "../api/biblioteca";
import type { BibliotecaDocumento, BibliotecaStandard, BibliotecaTipo } from "../types/biblioteca";

type Props = {
  standard: BibliotecaStandard;
  tipo: BibliotecaTipo;
  document: BibliotecaDocumento | null;
  onClose: () => void;
  onDownload: (document: BibliotecaDocumento) => void;
};

export function BibliotecaPreviewModal({ standard, tipo, document, onClose, onDownload }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!document) {
      setUrl(null);
      setError(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);

    void getBibliotecaPreview(standard, tipo, document.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(bibliotecaApiError(requestError, "No se pudo cargar la vista previa del PDF."));
        }
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document, standard, tipo]);

  return (
    <CertificationModal
      open={document !== null}
      title={document?.nombre_documento ?? "Vista previa PDF"}
      eyebrow="Biblioteca · Vista previa PDF"
      size="wide"
      onClose={onClose}
      error={error}
      footer={
        <>
          {url ? (
            <a
              className="iso27001-button iso27001-button-secondary"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={16} />
              <span>Abrir</span>
            </a>
          ) : null}
          <CertificationButton
            variant="secondary"
            icon={<Download size={16} />}
            onClick={() => document && onDownload(document)}
            disabled={!document}
          >
            Descargar
          </CertificationButton>
          <CertificationButton variant="primary" onClick={onClose}>
            Cerrar
          </CertificationButton>
        </>
      }
    >
      <div className="biblioteca-preview-body biblioteca-preview-body-shared">
        {!url && !error ? (
          <div className="biblioteca-preview-state">
            <Loader2 className="animate-spin" size={26} />
            Preparando documento…
          </div>
        ) : null}
        {url && document ? (
          <iframe
            className="biblioteca-pdf-frame"
            src={url}
            title={`Vista previa de ${document.nombre_documento}`}
          />
        ) : null}
      </div>
    </CertificationModal>
  );
}
